import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Copy, Zap, Info, Loader2, ChevronRight, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import {
  getElectricityPlans,
  verifyMeter,
  buyElectricity
} from "../../api/apiServices";

const PROFILE_PHONE_ROUTE = "/account";

// Nigerian meter numbers commonly run 10–13 digits depending on the
// disco and meter type (prepaid vs postpaid). Auto-verify fires once
// the digit count lands on any of these.
const AUTO_VERIFY_LENGTHS = [13, 14, 15];
const AUTO_VERIFY_DEBOUNCE_MS = 600;

// ── Local beneficiary storage ──────────────────────────────────────
// No backend beneficiary endpoint exists yet for electricity, so this
// persists recent meters on-device only (per browser/app install).
// Doesn't sync across devices — swap for a real API call if/when a
// beneficiaries backend exists.
const BENEFICIARY_STORAGE_KEY = "databling:electricity:beneficiaries";
const MAX_SAVED_BENEFICIARIES = 5;

function loadBeneficiaries() {
  try {
    const raw = localStorage.getItem(BENEFICIARY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistBeneficiary(entry) {
  try {
    const existing = loadBeneficiaries().filter(
      (b) => !(b.meterNumber === entry.meterNumber && b.disco === entry.disco)
    );
    const next = [entry, ...existing].slice(0, MAX_SAVED_BENEFICIARIES);
    localStorage.setItem(BENEFICIARY_STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadBeneficiaries();
  }
}

export default function ElectricityPayment() {
  const navigate = useNavigate();

  // --- UI & Stepper States ---
  const [step, setStep] = useState(1); // Step 1: Form, Step 2: Confirmation/PIN, Step 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsPhoneUpdate, setNeedsPhoneUpdate] = useState(false);
  const [copied, setCopied] = useState(false);

  // --- API Data States ---
  const [discos, setDiscos] = useState([]);
  const [loadingDiscos, setLoadingDiscos] = useState(true);

  // --- Form Inputs ---
  const [selectedDisco, setSelectedDisco] = useState("");
  const [meterNumber, setMeterNumber] = useState("");
  const [meterType, setMeterType] = useState("prepaid");
  const [amount, setAmount] = useState("");
  const [transactionPin, setTransactionPin] = useState("");

  // --- Verification Response Data ---
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [verifiedKey, setVerifiedKey] = useState(null); // "disco:meterNumber:meterType" of last successful verify
  const [verifyingMeter, setVerifyingMeter] = useState(false);

  // --- Purchase Response Data ---
  const [purchaseResult, setPurchaseResult] = useState(null);

  // --- Beneficiaries ---
  const [beneficiaries, setBeneficiaries] = useState([]);

  useEffect(() => {
    setBeneficiaries(loadBeneficiaries());
  }, []);

  // 1. Fetch Electricity Discos on Mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingDiscos(true);
        const response = await getElectricityPlans();

        if (response && response.plans) {
          setDiscos(response.plans);
        } else {
          setError("Failed to fetch available distribution companies.");
        }
      } catch (err) {
        console.error("Failed fetching disco plans", err);
        setError(err?.response?.data?.message || "Could not load electricity providers. Please try again.");
      } finally {
        setLoadingDiscos(false);
      }
    };

    fetchPlans();
  }, []);

  // 2. Meter verification (manual trigger — also called by the auto-verify effect below)
  const handleVerifyMeter = async () => {
    if (!selectedDisco || !meterNumber || !meterType) {
      setError("Please fill out all meter verification details.");
      return;
    }

    setError("");
    setNeedsPhoneUpdate(false);
    setVerifyingMeter(true);
    setVerifiedCustomer(null);

    try {
      const payload = {
        disco: selectedDisco,
        meterNumber,
        meterType
      };
      const res = await verifyMeter(payload);

      if (res && res.status) {
        setVerifiedCustomer(res);
        setVerifiedKey(`${selectedDisco}:${meterNumber}:${meterType}`);
      } else {
        setError(res?.message || "Meter verification failed. Please check details.");
      }
    } catch (err) {
      console.error("Meter verification error", err);
      setError(err?.response?.data?.message || "Error validating meter number. Verify connection.");
    } finally {
      setVerifyingMeter(false);
    }
  };

  // 2b. Auto-verify once the meter number reaches a plausible length.
  // Skips if this exact disco+meter+type combo was already verified,
  // and skips while a verification is already in flight.
  useEffect(() => {
    if (!selectedDisco || !meterType) return;
    if (!AUTO_VERIFY_LENGTHS.includes(meterNumber.length)) return;

    const key = `${selectedDisco}:${meterNumber}:${meterType}`;
    if (key === verifiedKey) return;
    if (verifyingMeter) return;

    const handle = setTimeout(() => {
      handleVerifyMeter();
    }, AUTO_VERIFY_DEBOUNCE_MS);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meterNumber, selectedDisco, meterType]);

  // 3. Move to PIN Confirmation Dialog
  const handleProceedToPay = () => {
    const activePlan = discos.find(p => p.plan_id === selectedDisco);
    const min = activePlan?.min_amount || 500;
    const max = activePlan?.max_amount || 1000000;

    if (!amount || Number(amount) < min || Number(amount) > max) {
      setError(`Amount must be between ₦${min.toLocaleString()} and ₦${max.toLocaleString()}`);
      return;
    }
    setError("");
    setStep(2); // Proceed to PIN confirmation step
  };

  // 4. Submit Final Purchase API
  const handleFinalPayment = async (e) => {
    e.preventDefault();
    if (transactionPin.length < 4) {
      setError("Please enter your 4-digit transaction PIN.");
      return;
    }

    setError("");
    setNeedsPhoneUpdate(false);
    setLoading(true);

    try {
      const payload = {
        disco: selectedDisco,
        meterNumber,
        meterType,
        amount: Number(amount),
        pin: transactionPin
      };

      const res = await buyElectricity(payload);
      setPurchaseResult(res);

      // Save as a beneficiary so it's quick to find next time.
      const entry = {
        id: `${selectedDisco}:${meterNumber}`,
        disco: selectedDisco,
        discoLabel: discos.find(d => d.plan_id === selectedDisco)?.plan_name || selectedDisco,
        meterNumber,
        meterType,
        customerName: verifiedCustomer?.customerName || null,
        label: `${verifiedCustomer?.customerName || selectedDisco} · ${meterNumber}`,
        lastUsed: Date.now()
      };
      setBeneficiaries(persistBeneficiary(entry));

      setStep(3); // Success Screen
    } catch (err) {
      console.error("Transaction purchase error", err);
      const errData = err?.response?.data;

      if (errData?.errorKey === "PHONE_NUMBER_REQUIRED") {
        setNeedsPhoneUpdate(true);
        setError(errData.message || "Please add a phone number to your profile before making this purchase.");
      } else {
        setError(errData?.message || "Transaction failed. Please verify your PIN or balance.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUseBeneficiary = (b) => {
    setSelectedDisco(b.disco || "");
    setMeterType(b.meterType || "prepaid");
    setMeterNumber(b.meterNumber || "");
    setVerifiedCustomer(null);
    setVerifiedKey(null);
    setError("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Top Header */}
      <header className="bg-white border-b px-4 py-3.5 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <button
          onClick={() => (step === 1 ? navigate(-1) : setStep(step - 1))}
          className="p-1 hover:bg-gray-100 rounded-full transition"
        >
          <ArrowLeft size={22} className="text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-gray-800">Electricity Payment</h1>
      </header>

      <main className="px-4 pt-6 max-w-md mx-auto">
        {needsPhoneUpdate ? (
          <div className="mb-4 bg-amber-50 text-amber-700 border border-amber-200 text-sm rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 font-medium">
              <Info size={18} />
              {error}
            </div>
            <button
              type="button"
              onClick={() =>
                navigate(PROFILE_PHONE_ROUTE, { state: { redirectTo: window.location.pathname } })
              }
              className="w-full h-10 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 transition"
            >
              Update phone number
            </button>
          </div>
        ) : (
          error && (
            <div className="mb-4 bg-red-50 text-red-600 border border-red-200 text-sm rounded-xl p-4 font-medium flex items-center gap-2">
              <Info size={18} />
              {error}
            </div>
          )
        )}

        <AnimatePresence mode="wait">
          {/* STEP 1: Verification & Amount Form */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Saved meters */}
              {beneficiaries.length > 0 && !selectedDisco && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Star size={12} /> Saved Meters
                  </p>
                  <div className="space-y-2">
                    {beneficiaries.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => handleUseBeneficiary(b)}
                        className="w-full flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-left hover:bg-blue-100 transition"
                      >
                        <div>
                          <p className="text-xs font-bold text-[#3866A3] uppercase tracking-wide">
                            {b.discoLabel}
                          </p>
                          <p className="text-sm text-gray-700 mt-0.5">{b.label}</p>
                        </div>
                        <ChevronRight size={18} className="text-[#3866A3]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Card */}
              <div className="bg-white rounded-2xl border p-5 shadow-sm space-y-4">
                {/* Provider Selector */}
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Select Distribution Company (DISCO)
                  </label>
                  {loadingDiscos ? (
                    <div className="h-12 bg-gray-50 animate-pulse rounded-xl border flex items-center px-4 text-sm text-gray-400">
                      Loading providers...
                    </div>
                  ) : (
                    <select
                      value={selectedDisco}
                      onChange={(e) => {
                        setSelectedDisco(e.target.value);
                        setVerifiedCustomer(null);
                        setVerifiedKey(null);
                      }}
                      className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition"
                    >
                      <option value="">-- Choose Disco --</option>
                      {discos.map((p) => (
                        <option key={p.plan_id} value={p.plan_id}>
                          {p.plan_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Meter Type selection */}
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Meter Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {["prepaid", "postpaid"].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setMeterType(type);
                          setVerifiedCustomer(null);
                          setVerifiedKey(null);
                        }}
                        className={`h-11 border text-sm font-semibold rounded-xl capitalize transition ${
                          meterType === type
                            ? "bg-[#E9EDF6] text-[#3866A3] border-[#3866A3]"
                            : "bg-gray-50 text-gray-600 border-gray-200"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Meter Number */}
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Meter Number
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Enter meter number"
                      value={meterNumber}
                      disabled={verifyingMeter}
                      onChange={(e) => {
                        setMeterNumber(e.target.value);
                        setVerifiedCustomer(null);
                      }}
                      className="flex-1 h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyMeter}
                      disabled={verifyingMeter || !meterNumber || !selectedDisco}
                      className="bg-[#3866A3] text-white px-4 rounded-xl text-sm font-semibold hover:bg-[#2e558a] transition disabled:opacity-50 flex items-center justify-center min-w-[90px]"
                    >
                      {verifyingMeter ? <Loader2 size={18} className="animate-spin" /> : "Verify"}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5">
                    We'll verify automatically once you finish typing your meter number.
                  </p>
                </div>

                {/* Verified Customer Details Box */}
                {verifiedCustomer && (
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3"
                  >
                    <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} />
                    <div className="text-xs">
                      <p className="font-bold text-emerald-800">Verified Customer details</p>
                      <p className="font-medium text-gray-700 mt-1 uppercase">
                        Name: {verifiedCustomer.customerName}
                      </p>
                      <p className="text-gray-500 uppercase mt-0.5">Address: {verifiedCustomer.address}</p>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Step 1 Part B: Financials */}
              {verifiedCustomer && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border p-5 shadow-sm space-y-4"
                >
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Amount (₦)
                    </label>
                    <input
                      type="number"
                      placeholder="Enter Amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 font-bold focus:outline-none focus:border-[#3866A3] transition"
                    />
                    <span className="text-[10px] text-gray-400 mt-1 block">
                      Min: ₦{discos.find(d => d.plan_id === selectedDisco)?.min_amount?.toLocaleString() || "500"} - Max: ₦{discos.find(d => d.plan_id === selectedDisco)?.max_amount?.toLocaleString() || "1,000,000"}
                    </span>
                  </div>

                  <button
                    onClick={handleProceedToPay}
                    className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition shadow-md mt-4"
                  >
                    Proceed to Pay
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* STEP 2: PIN Confirmation Popover/Slide */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl border p-6 shadow-md space-y-6 text-center"
            >
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto text-[#3866A3]">
                <Zap size={28} />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-800">Confirm Payment</h3>
                <p className="text-gray-500 text-sm mt-1">You are purchasing electricity units</p>
              </div>

              {/* Receipt Summary details */}
              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-500">Disco</span>
                  <span className="font-semibold text-gray-800 uppercase">{selectedDisco}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Meter Number</span>
                  <span className="font-semibold text-gray-800">{meterNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Account Name</span>
                  <span className="font-semibold text-gray-800 uppercase">{verifiedCustomer?.customerName}</span>
                </div>
                <hr className="border-gray-200 my-1" />
                <div className="flex justify-between text-base font-bold">
                  <span className="text-gray-700">Total Amount</span>
                  <span className="text-[#3866A3]">₦{Number(amount).toLocaleString()}</span>
                </div>
              </div>

              {/* PIN Input */}
              <form onSubmit={handleFinalPayment} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Enter Transaction PIN
                  </label>
                  <input
                    type="password"
                    maxLength={4}
                    value={transactionPin}
                    onChange={(e) => setTransactionPin(e.target.value)}
                    placeholder="••••"
                    className="w-32 h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-center text-xl font-bold tracking-widest focus:outline-none focus:border-[#3866A3] transition mx-auto block"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition disabled:opacity-75 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Processing Transaction...
                    </>
                  ) : (
                    `Pay ₦${Number(amount).toLocaleString()}`
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* STEP 3: Purchase Success Receipt screen */}
          {step === 3 && purchaseResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl border p-6 shadow-md text-center space-y-6"
            >
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto text-emerald-500">
                <CheckCircle2 size={40} />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-800">Purchase Successful!</h3>
                <p className="text-emerald-600 font-semibold text-sm mt-1">
                  {purchaseResult.message}
                </p>
              </div>

              {/* Token Display Box */}
              {purchaseResult.token && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
                  <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block mb-2">
                    Prepaid Token Code
                  </span>
                  <div className="flex items-center justify-center gap-3 animate-pulse">
                    <span className="text-xl font-black text-emerald-950 tracking-wider">
                      {purchaseResult.token}
                    </span>
                    <button
                      onClick={() => copyToClipboard(purchaseResult.token)}
                      className="p-1.5 bg-white hover:bg-emerald-100 border border-emerald-200 rounded-lg text-emerald-700 transition"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                  {copied && <span className="text-[10px] text-emerald-600 font-bold block mt-1.5">Copied!</span>}
                </div>
              )}

              {/* Units summary */}
              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2 text-left">
                {purchaseResult.units && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Allocated Units</span>
                    <span className="font-bold text-gray-800">{purchaseResult.units} kWh</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Meter Number</span>
                  <span className="font-medium text-gray-800">{meterNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction Status</span>
                  <span className="text-xs text-emerald-600 font-bold uppercase">SUCCESS</span>
                </div>
              </div>

              <button
                onClick={() => navigate("/")}
                className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition"
              >
                Back to Dashboard
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

// 0233250070825

// 0233250081483